<template>
  <div>
    <el-dialog :close-on-click-modal="false" :show-close="false" v-if="visible" :model-value="visible"
      @update:modelValue="(val) => { if (!val) modal.closeTrackRecord(); visible = val }" width="40%">
      <template #title>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span>Track Record</span>
          <el-button size="small" type="warning" @click="resetForm">Reset</el-button>
        </div>
      </template>

      <el-form :model="smsTrackRecord" :rules="rules" ref="formRef" label-width="120px" size="small">
        <el-form-item label="Search Slot: " prop="searchSlot">
          <el-autocomplete v-model="smsTrackRecord.slotCode" :fetch-suggestions="fetchSuggestions"
            placeholder="Search Slot Code" @select="handleSlotSelect" trigger-on-focus>
            <template #default="scope">
              <div>{{ scope?.item?.code }}</div>
            </template>
          </el-autocomplete>
        </el-form-item>
        <el-form-item label="Slot Code.: " prop="slotCode">
          <el-input v-model="smsTrackRecord.slotCode" disabled />
        </el-form-item>
        <el-form-item label="Search Product: " prop="lotNo">
          <el-autocomplete v-model="smsTrackRecord.lotNo" :fetch-suggestions="fetchProductSuggestions"
            placeholder="Search product lot no." @select="handleProductSelect" trigger-on-focus>
            <template #default="scope">
              <div>{{ scope?.item?.lotNo }}</div>
            </template>
          </el-autocomplete>
        </el-form-item>
        <el-form-item label="Product Name: " prop="name">
          <el-input v-model="smsTrackRecord.name" disabled />
        </el-form-item>
        <el-form-item label="SKU: " prop="sku">
          <div @click="modal.openSku()">
            <el-input v-model="smsTrackRecord.sku" readonly disabled />
          </div>
        </el-form-item>
        <template v-if="smsTrackRecord.productId">
          <el-form-item label="Category: " prop="categoryName">
            <el-input v-model="smsTrackRecord.categoryName" disabled />
          </el-form-item>
          <el-form-item label="Of: " prop="of">
            <el-input v-model="ofOptions.fromId(smsTrackRecord.of).name" disabled />
          </el-form-item>
          <el-form-item label="Record Note: " prop="recordNote">
            <el-input v-model="smsTrackRecord.recordNote" type="textarea" />
          </el-form-item>
        </template>
      </el-form>

      <template #footer>
        <el-button @click="modal.closeTrackRecord(); visible = false" size="small">Cancel</el-button>
        <el-button type="primary" @click="handleDialogConfirm" size="small">Confirm</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ofOptions, productStatusOptions } from '@/utils/constants'
import { fetchSlotList } from '@/api/slot'
import { fetchList as fetchProductList } from '@/api/product'
import { useModalStore } from '@/stores/modal'
import { useShopDataStore } from '@/stores/shopData'
import { useUserStore } from '@/stores/user'
import { useLoading } from '@/composables/useLoading'
import { defaultTrackRecord as defaultsmsTrackRecord } from './defaults'
import { getInventoryType } from '@/utils/helpers'

const modal = useModalStore()
const shopDataStore = useShopDataStore()
const userStore = useUserStore()
const { setLoading } = useLoading()
const formRef = ref(null)

const visible = ref(false)
const smsTrackRecord = ref({ ...defaultsmsTrackRecord })
const branchList = ref([])

const rules = {
  slotCode: [{ required: true, message: 'This field is required', trigger: 'blur' }],
  sku: [{ required: true, message: 'This field is required', trigger: 'change' }],
  // name: [{ required: true, message: 'This field is required', trigger: 'blur' }]
}

watch(() => modal.sku.selected, (val) => {
  if (val && visible.value) {
    smsTrackRecord.value.sku = val.code
    smsTrackRecord.value.skuPlace = val.place
  }
})

watch(() => modal.trackRecord.visible, async (val) => {
  if (val) {
    try {
      setLoading(true)
      branchList.value = shopDataStore.branches
      smsTrackRecord.value = { ...defaultsmsTrackRecord }

      const params = modal.trackRecord.data
      if (params) {
        const response = await fetchProductList({ keyword: params })
        handleProductSelect(response.data.list[0])
      } else {
        smsTrackRecord.value.place = userStore.defaultBranch?.id
      }
      visible.value = true
    } catch (e) {
      // error handled by request interceptor
    } finally {
      setLoading(false)
    }

  } else {
    visible.value = false
  }
}, { immediate: true })

function resetForm() {
  formRef.value?.resetFields()
}

async function fetchSuggestions(value, cb) {
  try {
    if (value && value.length > 1) {
      const response = await fetchSlotList({ keyword: value })
      cb(response.data.list || [])
    } else {
      cb([])
    }
  } catch (e) {
    cb([])
  }
}

async function fetchProductSuggestions(value, cb) {
  try {
    if (value && value.length > 1) {
      const response = await fetchProductList({ keyword: value, of: ofOptions.GD.id })
      cb(response.data.list || [])
    } else {
      cb([])
    }
  } catch (e) {
    cb([])
  }
}

function handleSlotSelect(item) {
  smsTrackRecord.value.slotId = item.id
  smsTrackRecord.value.slotCode = item.code
  smsTrackRecord.value.place = item.branchId
  smsTrackRecord.value.displayId = item.displayId
  formRef.value?.validateField('slotCode')
  shopDataStore.defaultSkuList.forEach(sku => {
    if (sku.place === item.branchId && sku.parentCategoryId === 60) {
      smsTrackRecord.value.sku = sku.code
      smsTrackRecord.value.skuPlace = sku.place
      return
    }
  })
}

function handleProductSelect(item) {
  smsTrackRecord.value.productId = item.id
  smsTrackRecord.value.lotNo = item.lotNo
  smsTrackRecord.value.name = item.name
  smsTrackRecord.value.categoryName = item.categoryName
  smsTrackRecord.value.of = item.of
}

async function handleDialogConfirm() {
  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) return

  const branchExist = smsTrackRecord.value.skuPlace == userStore.defaultBranch?.id
  if (!branchExist) {
    ElMessage.error('Employee Place and SKU Place should be the same.')
    return
  }
  if (smsTrackRecord.value.skuPlace !== smsTrackRecord.value.place) {
    ElMessage.error('Slot Place and Sku Place should be the same.')
    return
  }

  ElMessageBox.confirm(`Are you sure you want to create this track record?`, 'Warning', {
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    type: 'warning'
  }).then(async () => {
    smsTrackRecord.value.branchCode = branchList.value.find(b => b.id === smsTrackRecord.value.place)?.branchCode
    smsTrackRecord.value.inventoryType = getInventoryType(null, smsTrackRecord.value.sku)

    modal.trackRecord.data = { ...smsTrackRecord.value, _validated: true }
    modal.closeTrackRecord()
    visible.value = false
  }).catch(() => { })
}
</script>

<style></style>
